import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cycleService } from '../../services/cycleService';
import type { CycleCreate, CycleUpdate } from '../../models/cycle.schemas';
import { useToast } from '@/hooks/useToast';

/**
 * Hook for cycle mutations (create, update, close)
 * Provides cache invalidation and error handling
 *
 * @returns Mutation functions for cycles
 */
export function useCycleMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * Create cycle mutation
   * Validates for overlapping cycles before creating
   */
  const createCycle = useMutation({
    mutationFn: async (data: CycleCreate) => {
      // The service already validates overlapping in the create method
      return await cycleService.create(data);
    },
    onSuccess: () => {
      // Invalidate cycles queries to refetch
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
    onError: (error) => {
      console.error('Error creating cycle:', error);
    },
  });

  /**
   * Update cycle mutation with optimistic updates
   */
  const updateCycle = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CycleUpdate }) =>
      cycleService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['cycles'] });

      // Get previous data for rollback
      const previousCycles = queryClient.getQueriesData({ queryKey: ['cycles'] });

      // Optimistically update cache
      queryClient.setQueriesData({ queryKey: ['cycles'] }, (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((cycle: any) => (cycle.id === id ? { ...cycle, ...data } : cycle));
      });

      return { previousCycles };
    },
    onError: (error, variables, context) => {
      console.error('Error updating cycle:', error);

      // Rollback on error
      if (context?.previousCycles) {
        context.previousCycles.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Invalidate cycles queries to refetch
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
    },
  });

  /**
   * Close cycle mutation
   * Invalidates objectives related to the cycle
   */
  const closeCycle = useMutation({
    mutationFn: (id: string) => cycleService.close(id),
    onMutate: async (id) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['cycles'] });

      // Get previous data for rollback
      const previousCycles = queryClient.getQueriesData({ queryKey: ['cycles'] });

      // Optimistically update cache - mark as completed
      queryClient.setQueriesData({ queryKey: ['cycles'] }, (old: any) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((cycle: any) =>
          cycle.id === id ? { ...cycle, status: 'completed' } : cycle
        );
      });

      return { previousCycles };
    },
    onError: (error: any, variables, context) => {
      console.error('Error closing cycle:', error);
      const message = error?.message || 'Erro desconhecido ao encerrar ciclo';
      toast.error(`Erro ao encerrar ciclo: ${message}`);

      // Rollback on error
      if (context?.previousCycles) {
        context.previousCycles.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (data, cycleId) => {
      console.log('✅ [useCycleMutations] Ciclo encerrado com sucesso:', cycleId);
      toast.success('Ciclo encerrado com sucesso!');

      // Invalidate cycles queries
      queryClient.invalidateQueries({ queryKey: ['cycles'] });

      // Invalidate objectives for this cycle
      queryClient.invalidateQueries({ queryKey: ['objectives', cycleId] });
    },
  });

  /**
   * Activate cycle mutation (planning → active)
   * Enforces single active cycle constraint
   */
  const activateCycle = useMutation({
    mutationFn: (id: string) => cycleService.activateCycle(id),
    onSuccess: () => {
      // Invalidate cycles queries to refetch
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
      // Invalidate dashboard to show new active cycle
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: any) => {
      console.error('Error activating cycle:', error);
      const message = error?.message || 'Erro desconhecido ao ativar ciclo';
      toast.error(`Erro ao ativar ciclo: ${message}`);
    },
  });

  /**
   * Start review mutation (active → reviewing)
   */
  const startReview = useMutation({
    mutationFn: (id: string) => cycleService.startReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: any) => {
      console.error('Error starting review:', error);
      const message = error?.message || 'Erro desconhecido ao iniciar revisão';
      toast.error(`Erro ao iniciar revisão: ${message}`);
    },
  });

  /**
   * Complete cycle mutation (reviewing → completed)
   */
  const completeCycle = useMutation({
    mutationFn: (id: string) => cycleService.completeCycle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycles'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: any) => {
      console.error('Error completing cycle:', error);
      const message = error?.message || 'Erro desconhecido ao encerrar ciclo';
      toast.error(`Erro ao encerrar ciclo: ${message}`);
    },
  });

  /**
   * Delete cycle mutation
   * Simple deletion - no status transition needed
   */
  const deleteCycle = useMutation({
    mutationFn: (id: string) => cycleService.delete(id),
    onSuccess: (_, cycleId) => {
      console.log('✅ [useCycleMutations] Ciclo excluído com sucesso:', cycleId);
      toast.success('Ciclo excluído com sucesso!');

      // Invalidate cycles queries
      queryClient.invalidateQueries({ queryKey: ['cycles'] });

      // Invalidate objectives for this cycle
      queryClient.invalidateQueries({ queryKey: ['objectives', cycleId] });
    },
    onError: (error: any) => {
      console.error('Error deleting cycle:', error);
      const message = error?.message || 'Erro desconhecido ao excluir ciclo';
      toast.error(`Erro ao excluir ciclo: ${message}`);
    },
  });

  return {
    createCycle,
    updateCycle,
    deleteCycle,
    // Legacy exports (deprecated but kept for backward compatibility)
    closeCycle,
    activateCycle,
    startReview,
    completeCycle,
  };
}
